/// <amd-module name="Repos/Views/Pushes/Components/PushesView" />
import * as React from "react";
import { ICommitsDataProviderService } from "Repos/CommitsList/CommitsDataProviderService";
import { GitVersionDescriptor, GitVersionType } from "Repos/Common/Contracts/Git";
import { getDefaultCreateBranchAction } from "Repos/Common/ReposActionService";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getShortCommitId } from "Repos/Common/Util/Ref";
import { getVersionString, gitVersionStringToVersionDescriptor } from "Repos/Common/Util/Version";
import { getGitRepository, IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import { getInitialPushesFilterBarState, IPushesFilterBarState, PushesFilterBar, PushesFilterKeys } from "Repos/PushesList/Components/PushesFilterBar";
import { PushesTable } from "Repos/PushesList/Components/PushesTable";
import { IPush, IPushTableItem, PushTableItemType } from "Repos/PushesList/Contracts";
import { IPushesDataProviderService, PushesSearchFilterData } from "Repos/PushesList/PushesDataProviderService";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import * as Resources from "Repos/Views/Pushes/Resources";
import { ObservableArray, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { CustomHeader, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Filter } from "VSSUI/Utilities/Filter";
import { ITreeItem, ITreeItemAdd, ITreeItemEx, ITreeItemProvider, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData } from "VSSUI/ZeroData";
import { ClipboardButton } from "VSSUI/Clipboard";
import * as ClipboardResources from "VSSUI/Resources.Clipboard";

const top = 50;
const initialCommitPageSize = 25;
const additionalCommitPageSize = 150;

class PushesView extends VssComponent<{}> {
    private pushToCommitCount: { [pushId: number]: number };
    private fetchingCommits: boolean = false;
    private filterToggled: ObservableValue<boolean>;
    private loaded = false;
    private loadingRow: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>> | undefined;
    private pushesFilter: Filter;
    private pushCount: number = 0;
    private pushesMap: { [key: string]: ObservableValue<number> };
    private version: GitVersionDescriptor;
    private pushes: ITreeItemProvider<IPushTableItem | ObservableValue<IPushTableItem | undefined>>;
    private dataLoaded: boolean = false;
    private pushesExist: ObservableValue<boolean> = new ObservableValue(false);

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        const pushesDataService = this.context.pageContext.getService<IPushesDataProviderService>("IPushesDataProviderService");
        const historyService: IVssHistoryService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        this.pushes = new TreeItemProvider<IPushTableItem | ObservableValue<IPushTableItem | undefined>>([]);
        this.pushToCommitCount = {};
        this.dataLoaded = false;

        // Figure out current branch.  If one is not set in history, look it up in data provider and then update history
        const historyState = historyService.getState();
        let currentBranch = historyState.itemVersion;
        if (!currentBranch) {
            const vcViewModelDataProviderService = this.context.pageContext.getService<IVCViewModelDataProviderService>(
                "IVCViewModelDataProviderService"
            );

            currentBranch = "GB" + vcViewModelDataProviderService.getDefaultGitBranchName();
            historyState.itemVersion = currentBranch;
            historyService.replaceState(historyState);
        }

        this.version = gitVersionStringToVersionDescriptor(currentBranch);

        this.pushesMap = {};
        this.createSections(pushesDataService.getPushes(), false);
        this.dataLoaded = true;

        const initialFilterState = getInitialPushesFilterBarState(this.context.pageContext);
        this.pushesFilter = new Filter();
        this.pushesFilter.setState(initialFilterState, true);
        this.filterToggled = new ObservableValue<boolean>(this.pushesFilter.hasChangesToReset());
    }

    public render() {
        const commandBarItems: IHeaderCommandBarItem[] = [
            {
                iconProps: { iconName: "Filter" },
                id: "filter",
                ariaLabel: Resources.Filter,
                onActivate: () => (this.filterToggled.value = !this.filterToggled.value),
                subtle: true
            }
        ];

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow custom-scrollbar scroll-auto-hide">
                    <div className="flex-row flex-noshrink pushes-view-version-dropdown">
                        <VersionDropdown
                            onSelect={this.onNewVersionSelected}
                            initiallySelectedVersion={this.version}
                            providerOptions={{
                                onCreateBranchClick: getDefaultCreateBranchAction(this.context.pageContext),
                                viewTagsPivot: false
                            }}
                        />
                        <ClipboardButton
                            className="version-dropdown-branch-copy-button"
                            getContent={() => 
                                (this.version.version)
                            }
                            key="clipboard"
                            showCopiedTooltip={true}
                            tooltipProps={{ text: ClipboardResources.CopyToClipboard }}
                        />  
                    </div>
                    <CustomHeader className="pushes-view-header">
                        <div className="bolt-header-content-area flex-column flex-grow flex-self-stretch">
                            <div className="bolt-header-content-area commits-hub-header flex-row flex-grow flex-self-stretch">
                                <HeaderTitleArea>
                                    <HeaderTitleRow>
                                        <HeaderTitle titleSize={TitleSize.Large}>{Resources.Pushes}</HeaderTitle>
                                    </HeaderTitleRow>
                                </HeaderTitleArea>
                                <HeaderCommandBar items={commandBarItems} />
                            </div>
                        </div>
                    </CustomHeader>
                    <ConditionalChildren renderChildren={this.filterToggled}>
                        <PushesFilterBar pushesFilter={this.pushesFilter} onFilterChanged={this.performFilter} />
                    </ConditionalChildren>
                    <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                        <ReposMessages className="flex-noshrink" />
                        <Observer pushesExist={this.pushesExist}>
                            {(props: { pushesExist: boolean }) => {
                                if (!props.pushesExist) {
                                    return (
                                        <div className="page-content-top">
                                            <Card className="flex-grow flex-center no-push-results" contentProps={{ contentPadding: false }}>
                                                <ZeroData
                                                    className={"no-pushes"}
                                                    imagePath={getAssetLocation(
                                                        this.context.pageContext,
                                                        "ms.vss-code-web/pushes-view-content/Content/no-results.png"
                                                    )}
                                                    imageAltText={"Image"}
                                                    primaryText={Resources.NoResultsPrimaryText}
                                                    secondaryText={Resources.NoResultsSecondaryText}
                                                />
                                            </Card>
                                        </div>
                                    );
                                } else {
                                    return (
                                        <Card className="bolt-table-card flex" contentProps={{ contentPadding: false }}>
                                            <PushesTable
                                                pushes={this.pushes}
                                                loadMore={this.loadMore}
                                                onToggle={(
                                                    event: React.SyntheticEvent<HTMLElement>,
                                                    treeItem: ITreeItemEx<IPushTableItem | ObservableValue<IPushTableItem>>
                                                ) => {
                                                    // Call toggle on the tree item provider
                                                    this.pushes.toggle(treeItem.underlyingItem);

                                                    if (treeItem.underlyingItem.data) {
                                                        const tableItem = ObservableLike.getValue<IPushTableItem>(treeItem.underlyingItem.data);
                                                        // Invoke toggle on the tree item
                                                        tableItem.onToggle!(treeItem.underlyingItem);
                                                    }
                                                }}
                                            />
                                        </Card>
                                    );
                                }
                            }}
                        </Observer>
                    </div>
                </Page>
            </Surface>
        );
    }

    public componentDidMount() {
        this.handleLoaded();
    }

    private loadMore = (treeItem: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>> | undefined) => {
        if (!this.fetchingCommits) {
            if (treeItem) {
                this.loadCommits(treeItem, additionalCommitPageSize);
            } else {
                if (this.dataLoaded) {
                    this.dataLoaded = false;
                    const filterBarState: IPushesFilterBarState = {
                        dateFrom: this.pushesFilter.getFilterItemValue<Date>(PushesFilterKeys.DateFrom),
                        dateTo: this.pushesFilter.getFilterItemValue<Date>(PushesFilterKeys.DateTo),
                        excludeIdentities: this.pushesFilter.getFilterItemValue<IIdentity[]>(PushesFilterKeys.ExcludeFrom),
                        pushedBy: this.pushesFilter.getFilterItemValue<IIdentity>(PushesFilterKeys.PushedBy)
                    };

                    this.loadData(filterBarState, false);
                }
            }
        }
    };

    private performFilter = (filterbarState: IPushesFilterBarState) => {
        this.loadData(filterbarState, true).then(() => {
            const announcement = this.pushCount > 0 ? format(Resources.PushesCount, this.pushCount) : Resources.NoResultsPrimaryText;
            announce(announcement);
        });
    };

    private onNewVersionSelected = (event: React.SyntheticEvent<HTMLElement>, item: any): void => {
        const currentVersionString = getVersionString(this.version);
        const newVersion = item.data.versionDescriptor;
        const newVersionString = getVersionString(newVersion);
        if (currentVersionString !== newVersionString) {
            // Update the state
            const historyService: IVssHistoryService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            const historyState = historyService.getState();
            historyState.itemVersion = newVersionString;
            historyService.replaceState(historyState);

            // Create route values
            const routeValues: { [keey: string]: string } = {};
            routeValues.itemVersion = newVersionString;

            if (historyState.fromDate) {
                routeValues.fromDate = historyState.fromDate;
            }

            if (historyState.toDate) {
                routeValues.toDate = historyState.toDate;
            }

            if (historyState.excludeUsers) {
                routeValues.excludeUsers = historyState.excludeUsers;
            }

            if (historyState.userId) {
                routeValues.userId = historyState.userId;
            }

            if (historyState.userName) {
                routeValues.userName = historyState.userName;
            }

            const vcViewModelDataProviderService = this.context.pageContext.getService<IVCViewModelDataProviderService>(
                "IVCViewModelDataProviderService"
            );
            const vcViewModel = vcViewModelDataProviderService.getViewModel();

            const reposRoutingService = this.context.pageContext.getService<IReposRoutingService>("IReposRoutingService");
            const newVersionUrl = reposRoutingService.getPushesUrl(
                vcViewModel ? vcViewModel.gitRepository!.name : "",
                vcViewModel ? vcViewModel.gitRepository!.project.name : "",
                routeValues
            );
            onClickFPS(this.context.pageContext, newVersionUrl, true, event);

            // Save the user's preferred branch
            if (newVersion.versionType === GitVersionType.Branch) {
                const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                settingsService.setEntries(
                    { "Git/DefaultUserBranch": newVersion.version },
                    SettingsUserScope.Me,
                    "Repository",
                    getGitRepository(this.context.pageContext)!.id
                );
            }
        }
    };

    private onToggle = (tableItem: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem>>) => {
        const pushItem = ObservableLike.getValue<IPushTableItem>(tableItem.data);
        // Fetch the commits associated with the push and insert them into the view
        if (tableItem.expanded && !pushItem.commitsFetched) {
            this.fetchingCommits = true;
            const push = pushItem.underlyingItem as IPush;
            push.childCount = 1;

            this.loadCommits(tableItem, initialCommitPageSize);
        }
    };

    private loadCommits(treeItem: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>>, pageSize: number): void {
        this.fetchingCommits = true;
        const pushItem = ObservableLike.getValue<IPushTableItem | undefined>(treeItem.data);
        if (pushItem) {
            const push = pushItem.underlyingItem as IPush;
            // lookup how many commits fetched
            let skip: number | undefined = this.pushToCommitCount[push.pushId];
            if (!skip) {
                skip = 0;
                this.pushToCommitCount[push.pushId] = skip;
            }

            const commitsDataService = this.context.pageContext.getService<ICommitsDataProviderService>("ICommitsDataProviderService");
            commitsDataService.fetchCommitsForPush(push.push.push, skip, pageSize).then(commitResults => {
                this.pushes.splice(treeItem, [treeItem.childItems![treeItem.childItems!.length - 1]]);
                const tableItems: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>>[] = [];
                if (commitResults) {
                    const reposRoutingService = this.context.pageContext.getService<IReposRoutingService>("IReposRoutingService");
                    for (const c of commitResults.commits) {
                        const linkProps = {
                            href: reposRoutingService.getCommitUrl(commitResults.repository, commitResults.project, c.commitId)
                        };
                        tableItems.push({
                            data: {
                                itemType: PushTableItemType.Commit,
                                linkProps,
                                underlyingItem: {
                                    linkProps,
                                    shortCommitId: getShortCommitId(c.commitId),
                                    comment: c.comment,
                                    commit: c,
                                    pullRequest: new ObservableValue(undefined),
                                    repositoryId: commitResults.repository,
                                    statuses: new ObservableArray(),
                                    tags: new ObservableArray()
                                },
                                expanded: false,
                                commitsFetched: false
                            }
                        });
                    }

                    if (commitResults.commits.length === pageSize) {
                        tableItems.push({ data: new ObservableValue(undefined) });
                    }
                    this.pushToCommitCount[push.pushId] += commitResults.commits.length;
                }

                const itemsToAdd: ITreeItemAdd<IPushTableItem | ObservableValue<IPushTableItem | undefined>> = { items: [] };

                // Insert after the last item in tree node
                itemsToAdd.insertAfter = treeItem.childItems ? treeItem.childItems[treeItem.childItems.length - 1] : undefined;

                itemsToAdd.items.push(...tableItems);
                this.pushes.splice(treeItem, undefined, [itemsToAdd]);
                this.fetchingCommits = false;
                pushItem.commitsFetched = true;
            });
        }
    }

    private loadData(filterbarState: IPushesFilterBarState, resetSections: boolean): Promise<void> {
        const searchCriteria: PushesSearchFilterData = {};
        searchCriteria.fromDate = filterbarState.dateFrom;
        searchCriteria.toDate = filterbarState.dateTo;

        if (filterbarState.excludeIdentities && filterbarState.excludeIdentities.length > 0) {
            searchCriteria.excludeUsers = filterbarState.excludeIdentities.map(identity => identity.localId).join("*");
        }

        if (filterbarState.pushedBy) {
            searchCriteria.userId = filterbarState.pushedBy.localId;
            searchCriteria.userName = filterbarState.pushedBy.displayName;
        }

        const pushesDataService = this.context.pageContext.getService<IPushesDataProviderService>("IPushesDataProviderService");
        return pushesDataService.fetchPushes(searchCriteria, resetSections ? 0 : this.pushCount, top + 1).then(pushes => {
            if (resetSections) {
                this.pushCount = 0;
                this.pushesMap = {};
                this.pushToCommitCount = {};
                this.loadingRow = undefined;
                this.createSections(pushes, resetSections);
            } else {
                this.createSections(pushes, resetSections);
            }
            this.dataLoaded = true;
        });
    }

    private handleLoaded() {
        if (!this.loaded) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive("Repos", "pushes-view");
            this.loaded = true;
        }
    }

    /**
     * Function to group the pushes by date
     */
    private createSections(pushes: IPush[], resetSections: boolean): void {
        // If a loading row is present, remove it
        if (this.loadingRow) {
            this.pushes.splice(undefined, [this.loadingRow]);
            this.loadingRow = undefined;
        }

        if (pushes) {
            this.pushCount += pushes.length;
            let currentSectionCount: ObservableValue<number> | undefined = undefined;
            const itemsToAdd: ITreeItemAdd<IPushTableItem | ObservableValue<IPushTableItem | undefined>> = { items: [] };

            for (const pushEntry of pushes) {
                const pushDate = pushEntry.pushDate.toLocaleDateString();
                currentSectionCount = this.pushesMap[pushDate];

                // If we have not see this section yet, start keeping track of number of pushes
                // and add a title row to the table.
                if (!currentSectionCount) {
                    currentSectionCount = new ObservableValue(0);
                    this.pushesMap[pushDate] = currentSectionCount;

                    const sectionTreeItem: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>> = {
                        data: {
                            itemType: PushTableItemType.Title,
                            expanded: false,
                            commitsFetched: false,
                            underlyingItem: { text: pushDate, pushCount: currentSectionCount }
                        }
                    };

                    itemsToAdd.items.push(sectionTreeItem);
                }

                currentSectionCount.value++;

                const treeItem: ITreeItem<IPushTableItem | ObservableValue<IPushTableItem | undefined>> = {
                    data: {
                        itemType: PushTableItemType.Push,
                        underlyingItem: pushEntry,
                        expanded: false,
                        onToggle: this.onToggle,
                        commitsFetched: false
                    },
                    childItems: []
                };
                treeItem.childItems!.push({ data: new ObservableValue(undefined) });
                itemsToAdd.items.push(treeItem);
            }

            if (resetSections) {
                this.pushes.clear();
            } else if (this.pushes.roots && this.pushes.roots.length > 0) {
                itemsToAdd.insertAfter = this.pushes.roots[this.pushes.roots.length - 1];
            }
            this.pushes.splice(undefined, undefined, [itemsToAdd]);

            if (this.pushesExist.value !== this.pushCount > 0) {
                this.pushesExist.value = this.pushCount > 0;
            }

            // if there is not a loading row and the max amount of data has been fetched
            // then add a loading row.
            if (!this.loadingRow && pushes.length > top) {
                const loadingRowAdd: ITreeItemAdd<IPushTableItem | ObservableValue<IPushTableItem | undefined>> = { items: [] };
                loadingRowAdd.insertAfter = this.pushes.roots[this.pushes.roots.length - 1];

                const asyncRow = new ObservableValue<IPushTableItem | undefined>(undefined);
                this.loadingRow = { data: asyncRow };
                loadingRowAdd.items.push(this.loadingRow);

                this.pushes.splice(undefined, undefined, [loadingRowAdd]);
            }
        }
    }
}

Components.add("vss-code-web-pushes-view", PushesView);
